import getConfig from 'next/config';

import { buildFaqPageUrl } from '@/features/faq-page/utils/route-utils';
import { buildJobCategoryPageUrl } from '@/features/job-category-page/utils/route-utils';
import { SpecialtyPage } from '@/features/specialty-page';
import { Job } from '@/services/public-jobs.service';
import { buildJobCanonicalLink } from '@/utils/canonical-link-utils';
import { isNurse, isParamNurse } from '@/utils/discipline-utils';
import { buildQueryString } from '@/utils/query-utils';
import { slug } from '@/utils/slug-utils';

export type JobSearchQuery = {
  discipline?: string;
  specialty?: string;
  ordering?: 'highest_pay' | 'relevancy';
  locations?: string[] | string;
  compactState?: boolean;
};

type JobFullRouteParams = Pick<
  Job,
  'specialty' | 'discipline' | 'title' | 'code' | 'id'
>;

export const routes = {
  /**
   * domain() is responsible for referencing the entrypoint domain. eg: https://nomadhealth.com
   * this is use on the sitemap generation, canonical link and on clicking in a job post.
   * **/
  domain: () => getConfig()?.publicRuntimeConfig?.nomadHost || '',
  home: () => '/',
  job: (id: string) => `${routes.domain()}/job/${id}`,
  jobFull: ({ specialty, discipline, title, code, id }: JobFullRouteParams) => {
    if (!specialty || !discipline || !title || !code) {
      if (!id) throw new Error('jobFull route missing required params');

      return routes.job(id);
    }

    const isNurse = isParamNurse(discipline);
    const configDomain = routes.domain();

    /**
     *  if it's an url, grab the origin from it and use it as the base domain
     *  if it's an empty string, it will use the relative path
     */
    const baseDomain = configDomain
      ? new URL(configDomain).origin
      : configDomain;

    return [
      baseDomain,
      'healthcare-jobs',
      isNurse ? 'nurse' : 'allied',
      isNurse ? 'travel-nurse' : 'travel-allied',
      ...(!isNurse ? [slug(discipline)] : []),
      slug(specialty),
      slug(title),
      code,
    ].join('/');
  },
  jobSearch: ({ specialty, ...query }: JobSearchQuery = {}) => {
    const queryString = buildQueryString({
      ...query,
      ...(specialty ? { specializations: specialty } : {}),
    });
    const url = `${routes.domain()}/jobs/search`;
    return queryString ? `${url}?${queryString}` : url;
  },
  register: (options?: { pathOnly?: boolean }) => {
    const path = getConfig().publicRuntimeConfig.flags.auth0
      ? '/register'
      : '/auth/register';

    if (options?.pathOnly) {
      return path;
    }

    return `${routes.domain()}${path}`;
  },
  signIn: (options?: { pathOnly?: boolean }) => {
    const path = getConfig().publicRuntimeConfig.flags.auth0
      ? '/authenticate'
      : '/sign-in';

    if (options?.pathOnly) {
      return path;
    }

    return `${routes.domain()}${path}`;
  },
  facilities: () => '/facilities',
  specialtyPage: (specialtyPage: SpecialtyPage) => {
    const { specialty } = specialtyPage;
    const discipline = specialty?.discipline;
    if (!specialty?.slug || !discipline?.slug) {
      return '/';
    }

    return [
      isNurse(discipline.disciplineType) ? 'nurse' : 'allied',
      discipline.slug,
      specialty.slug,
    ].join('/');
  },
  jobCategoryPage: buildJobCategoryPageUrl,
  buildJobCanonicalLink: buildJobCanonicalLink,
  faqPage: buildFaqPageUrl,
};
